import React, { ReactNode } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';
import { Lock } from 'lucide-react';

interface PermissionGuardProps {
  children: ReactNode;
  permissions?: string[];
  roles?: string[];
  requireAll?: boolean; // If true, requires ALL permissions/roles. If false (default), requires ANY
  fallback?: ReactNode;
  showFallback?: boolean;
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permissions = [],
  roles = [],
  requireAll = false,
  fallback,
  showFallback = true,
}) => {
  const { hasAnyPermission, hasAnyRole, hasPermission, hasRole, isLoading, permissions: userPermissions, roles: userRoles } = usePermissions();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-sm text-gray-500">Checking permissions...</span>
      </div>
    );
  }

  console.group('🛡️ PERMISSION GUARD CHECK');
  console.log('📋 Required permissions:', permissions);
  console.log('👤 Required roles:', roles);
  console.log('🔍 User has permissions:', userPermissions);
  console.log('👑 User has roles:', userRoles);
  console.log('⚙️ Require all:', requireAll);

  let hasAccess = true;
  let permissionCheckResult = true;
  let roleCheckResult = true;

  // Check permissions
  if (permissions.length > 0) {
    if (requireAll) {
      permissionCheckResult = permissions.every(permission => hasPermission(permission));
      console.log('🔒 Permission check (ALL required):', permissions.map(p => ({ permission: p, hasIt: hasPermission(p) })));
    } else {
      permissionCheckResult = hasAnyPermission(permissions);
      console.log('🔓 Permission check (ANY required):', permissions.map(p => ({ permission: p, hasIt: hasPermission(p) })));
    }
    hasAccess = permissionCheckResult;
    console.log('✅ Permission check result:', permissionCheckResult);
  } else {
    console.log('⚪ No permissions required');
  }

  // Check roles (only if permissions check passed or no permissions specified)
  if (hasAccess && roles.length > 0) {
    if (requireAll) {
      roleCheckResult = roles.every(role => hasRole(role));
      console.log('🔒 Role check (ALL required):', roles.map(r => ({ role: r, hasIt: hasRole(r) })));
    } else {
      roleCheckResult = hasAnyRole(roles);
      console.log('🔓 Role check (ANY required):', roles.map(r => ({ role: r, hasIt: hasRole(r) })));
    }
    hasAccess = roleCheckResult;
    console.log('✅ Role check result:', roleCheckResult);
  } else if (roles.length > 0) {
    console.log('⚪ Role check skipped (permission check failed)');
  } else {
    console.log('⚪ No roles required');
  }

  console.log('🎯 FINAL ACCESS DECISION:', hasAccess ? '✅ GRANTED' : '❌ DENIED');
  console.groupEnd();

  if (hasAccess) {
    return <>{children}</>;
  }

  // Show custom fallback or default access denied message
  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showFallback) {
    return null;
  }

  // Default access denied UI
  return (
    <div className="flex items-center justify-center p-6 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
      <div className="text-center">
        <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Access Restricted
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          You don't have permission to access this content.
        </p>
        {(permissions.length > 0 || roles.length > 0) && (
          <div className="text-xs text-gray-400 dark:text-gray-500">
            <p>Required {requireAll ? 'all' : 'any'} of:</p>
            {permissions.length > 0 && (
              <p>Permissions: {permissions.join(', ')}</p>
            )}
            {roles.length > 0 && (
              <p>Roles: {roles.join(', ')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionGuard;
